import { Controller } from "@hotwired/stimulus"

const NOOP = () => {}
const VIEWPORT_MARGIN = 8
const HOVER_CLOSE_DELAY_MS = 150

// Connects to data-controller="dropdown"
export default class extends Controller {
  static targets = ["button", "menu"]
  static values = { closeOnMenuClick: { type: Boolean, default: true } }

  connect() {
    this.#enableTouchClickDelegation()
    document.addEventListener("click", this.#closeOnDocumentClick)
    document.addEventListener("keydown", this.#closeOnEscape)
  }

  disconnect() {
    clearTimeout(this.hoverCloseTimer)
    document.removeEventListener("click", this.#closeOnDocumentClick)
    document.removeEventListener("keydown", this.#closeOnEscape)
  }

  toggle(event) {
    event.preventDefault()

    if (this.openedByHover) this.#pin()
    else this.#setOpen(!this.#isOpen)
  }

  // タッチの pointerenter は click を伴わないこと (スクロールの開始など) があり、開いたまま残るのでマウスに限る
  hoverOpen(event) {
    clearTimeout(this.hoverCloseTimer)
    if (event.pointerType !== "mouse" || this.#isOpen) return

    this.#setOpen(true)
    this.openedByHover = true
  }

  // トグルからメニューへポインターを移す間の隙間で閉じないよう、少し待ってから閉じる
  hoverClose() {
    if (!this.openedByHover) return

    clearTimeout(this.hoverCloseTimer)
    this.hoverCloseTimer = setTimeout(() => this.#setOpen(false), HOVER_CLOSE_DELAY_MS)
  }

  #pin() {
    clearTimeout(this.hoverCloseTimer)
    this.openedByHover = false
  }

  get #isOpen() {
    return !this.menuTarget.classList.contains("hidden")
  }

  // 自分のトグル内のクリックだけ無視するので、メニュー内のクリックでも、別の dropdown を開いたときにも
  // 閉じる (Bootstrap の autoClose 既定と同じ)
  #closeOnDocumentClick = (event) => {
    if (this.buttonTarget.contains(event.target)) return
    if (!this.closeOnMenuClickValue && this.menuTarget.contains(event.target)) return

    this.#setOpen(false)
  }

  #closeOnEscape = (event) => {
    if (event.key !== "Escape") return

    // メニュー内の項目に focus があると復帰先を失うので、閉じる前にトグルへ戻す
    if (this.menuTarget.contains(document.activeElement)) this.buttonTarget.focus()
    this.#setOpen(false)
  }

  // iOS Safari は非インタラクティブな要素へのタップで click を合成しないので、そのままだと余白タップで
  // document のハンドラが発火せず閉じない。body 直下に空の listener を張ると合成されるようになる
  // (bootstrap/js/src/dropdown.js と同じ回避)
  #enableTouchClickDelegation() {
    if (!("ontouchstart" in document.documentElement)) return

    for (const child of document.body.children) child.addEventListener("mouseover", NOOP)
  }

  // Popper の preventOverflow 相当。幅の広いメニューがビューポートからはみ出す分だけ水平に押し戻す
  #keepInsideViewport() {
    this.menuTarget.style.translate = ""

    const { left, right } = this.menuTarget.getBoundingClientRect()
    const overflowLeft = VIEWPORT_MARGIN - left
    const overflowRight = right + VIEWPORT_MARGIN - document.documentElement.clientWidth

    if (overflowLeft > 0) this.menuTarget.style.translate = `${overflowLeft}px`
    else if (overflowRight > 0) this.menuTarget.style.translate = `${-overflowRight}px`
  }

  #setOpen(open) {
    clearTimeout(this.hoverCloseTimer)
    this.openedByHover = false
    this.menuTarget.classList.toggle("hidden", !open)
    this.buttonTarget.setAttribute("aria-expanded", open)

    if (open) this.#keepInsideViewport()
  }
}
